<template>
	<app-content class="h-panel initial-import-center">
		<div class="h-panel-bar">
			<span class="h-panel-title">科目余额表一键初始化</span>
			<div class="pull-right">
				<Button @click="goBack" icon="h-icon-left">返回期初页</Button>
			</div>
		</div>

		<div class="padding import-main">
			<!-- 步骤指引 -->
			<div class="steps-bar">
				<div class="step" :class="{active: step >= 1, done: step > 1}">
					<span class="step-num">1</span>
					<span class="step-text">上传余额表</span>
				</div>
				<div class="step-line" :class="{active: step > 1}"></div>
				<div class="step" :class="{active: step >= 2, done: step > 2}">
					<span class="step-num">2</span>
					<span class="step-text">预演检查</span>
				</div>
				<div class="step-line" :class="{active: step > 2}"></div>
				<div class="step" :class="{active: step >= 3}">
					<span class="step-num">3</span>
					<span class="step-text">确认执行</span>
				</div>
			</div>

			<!-- 上传区域 -->
			<div class="panel-card upload-card">
				<div class="card-header">
					<div class="card-title">导入文件</div>
					<div class="encoding-tag" v-if="currentAccountSets && currentAccountSets.encoding">编码规则：{{currentAccountSets.encoding}}</div>
				</div>

				<div class="upload-zone" :class="{dragging: isDragging, 'has-file': balanceFile}" @dragover.prevent="isDragging = true" @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop">
					<div v-if="!balanceFile" class="upload-placeholder" @click="triggerBalanceUpload">
						<i class="h-icon-upload upload-icon"></i>
						<div class="upload-text">点击或拖拽上传科目余额表</div>
						<div class="upload-hint">支持 .xls / .xlsx 格式</div>
					</div>
					<div v-else class="upload-file-info">
						<i class="h-icon-file upload-file-icon"></i>
						<div class="upload-file-detail">
							<div class="upload-file-name">{{balanceFileName}}</div>
							<div class="upload-file-size">{{fileSizeText}}</div>
						</div>
						<span class="text-link" @click="clearFile">重新选择</span>
					</div>
					<input
						ref="balanceFile"
						type="file"
						accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
						style="display: none"
						@change="balanceFileChange"
					>
				</div>

				<!-- 预演状态 -->
				<div class="status-box" :class="statusClass">
					<div class="status-title">
						<i :class="statusIcon"></i>
						{{statusTitle}}
					</div>
					<div v-if="previewError" class="status-reason">{{previewError}}</div>
					<div v-if="previewPlan && previewPlan.trialBalanced === false" class="status-reason">
						{{previewPlan.blockingReason || '试算不平衡，请先调整映射'}}
					</div>
					<div v-if="previewPlan && previewPlan.encodingDetected" class="status-meta">
						识别编码层级：{{previewPlan.encodingDetected}}
						<span v-if="previewPlan.encodingHasExtraLevels" class="warning-text">（超过系统支持上限{{maxSubjectLevel}}级，已按前{{maxSubjectLevel}}级处理）</span>
					</div>
					<div v-if="previewPlan && previewPlan.encodingUpgraded" class="status-meta upgrade-notice">
						账套编码将自动升级：{{previewPlan.encodingBefore}} &rarr; {{previewPlan.encodingAfter}}
					</div>
				</div>

				<!-- 汇总指标 -->
				<div class="summary-grid" v-if="previewPlan">
					<div class="summary-item">
						<div class="summary-label">末级科目</div>
						<div class="summary-value">{{previewPlan.importRowTotal || 0}}</div>
					</div>
					<div class="summary-item">
						<div class="summary-label">有金额</div>
						<div class="summary-value">{{previewPlan.amountRowTotal || 0}}</div>
					</div>
					<div class="summary-item">
						<div class="summary-label">自动新增</div>
						<div class="summary-value">{{(previewPlan.mappingSummary && previewPlan.mappingSummary.autoCreate) || 0}}</div>
					</div>
					<div class="summary-item" :class="{'has-warning': (previewPlan.mappingSummary && previewPlan.mappingSummary.needConfirm) > 0}">
						<div class="summary-label">需确认</div>
						<div class="summary-value warning-text">{{(previewPlan.mappingSummary && previewPlan.mappingSummary.needConfirm) || 0}}</div>
					</div>
					<div class="summary-item">
						<div class="summary-label">试算借方</div>
						<div class="summary-value">{{(previewPlan.trialDebit || 0) | numFormat}}</div>
					</div>
					<div class="summary-item">
						<div class="summary-label">试算贷方</div>
						<div class="summary-value">{{(previewPlan.trialCredit || 0) | numFormat}}</div>
					</div>
					<div class="summary-item">
						<div class="summary-label">差额</div>
						<div class="summary-value" :class="previewPlan.trialBalanced ? 'ok-text' : 'warning-text'">{{(previewPlan.trialDifference || 0) | numFormat}}</div>
					</div>
					<div class="summary-item">
						<div class="summary-label">期初条数</div>
						<div class="summary-value">{{previewPlan.initialBalanceTotal || 0}}</div>
					</div>
					<div class="summary-item span-full" :class="previewPlan.trialBalanced ? 'balance-ok' : 'balance-warn'">
						<div class="summary-label">平衡状态</div>
						<div class="summary-value" :class="previewPlan.trialBalanced ? 'ok-text' : 'warning-text'">
							{{previewPlan.trialBalanced ? '平衡 - 可以执行导入' : '不平衡 - 请调整映射后重新预演'}}
						</div>
					</div>
				</div>

				<!-- 操作按钮 -->
				<div class="card-actions">
					<Button color="blue" :loading="previewLoading" :disabled="!balanceFile" @click="runPreview(false)">
						{{previewPlan ? '重新预演' : '导入预演'}}
					</Button>
					<Button color="green" :loading="executeLoading" :disabled="!canExecute" @click="confirmAndExecute">
						一键执行导入
					</Button>
				</div>
			</div>

			<!-- 映射调整区域 -->
			<div class="panel-card" v-if="previewPlan">
				<div class="card-header">
					<div class="card-title">科目映射明细</div>
					<span class="muted-text">共 {{previewMappings.length}} 条映射，当前显示 {{filteredMappings.length}} 条</span>
				</div>

				<div class="toolbar-row">
					<input v-model.trim="keyword" type="text" class="h-input toolbar-input" placeholder="搜索编码或名称...">
					<Select
						v-model="strategyFilter"
						:datas="strategyOptions"
						keyName="key"
						titleName="title"
						:deletable="false"
						class="toolbar-select"
					/>
					<Checkbox v-model="onlyNeedConfirm">只看需确认</Checkbox>
				</div>

				<div class="toolbar-row">
					<input v-model.trim="batchTargetCode" type="text" class="h-input toolbar-input" placeholder="批量填充目标科目编码（对当前筛选结果生效）">
					<Button size="s" color="primary" @click="applyBatchTarget">批量填充</Button>
					<Button size="s" @click="clearAllManualTarget">清空调整</Button>
				</div>

				<div class="toolbar-row">
					<input
						v-model.trim="subjectLookupKeyword"
						type="text"
						placeholder="搜索目标科目编码或名称"
						class="h-input toolbar-input"
						@keyup.enter="searchSubjects"
					>
					<Button size="s" :loading="subjectLookupLoading" @click="searchSubjects">检索</Button>
				</div>
				<div class="lookup-list" v-if="subjectLookupOptions.length">
					<div class="lookup-item" v-for="item in subjectLookupOptions" :key="item.code" @click="useLookupSubject(item)">
						<span class="lookup-code">{{item.code}}</span>
						<span class="lookup-name">{{item.name}}</span>
						<span class="lookup-dir">{{item.balanceDirection || '-'}}</span>
					</div>
				</div>

				<Table :datas="filteredMappings" border class="mapping-table">
					<TableItem title="来源科目" :width="260">
						<template slot-scope="{data}">
							<div class="subject-line">
								<span class="subject-code">{{data.sourceCode}}</span>
								<span class="subject-name">{{data.sourceName}}</span>
							</div>
						</template>
					</TableItem>
					<TableItem title="映射目标" :width="260">
						<template slot-scope="{data}">
							<div class="subject-line">
								<span class="subject-code" :class="{'auto-create-code': data.strategy === 'auto_create'}">{{data.targetCode || '-'}}</span>
								<span class="subject-name">{{data.targetName || '-'}}</span>
							</div>
						</template>
					</TableItem>
					<TableItem title="策略" :width="100" align="center">
						<template slot-scope="{data}">
							<span class="strategy-tag" :class="'strategy-' + data.strategy">{{formatMappingStrategy(data.strategy)}}</span>
						</template>
					</TableItem>
					<TableItem title="方向" :width="60" align="center">
						<template slot-scope="{data}">
							<span :class="data.balanceDirection === '借' ? 'dir-debit' : 'dir-credit'">{{data.balanceDirection || '-'}}</span>
						</template>
					</TableItem>
					<TableItem title="期初余额" :width="130" align="right">
						<template slot-scope="{data}">
							<span :class="{'zero-amount': !data.endingBalance || data.endingBalance == 0}">{{data.endingBalance | numFormat}}</span>
						</template>
					</TableItem>
					<TableItem title="状态" :width="80" align="center">
						<template slot-scope="{data}">
							<span :class="data.needConfirm ? 'warning-text' : 'ok-text'">{{data.needConfirm ? '需确认' : '自动'}}</span>
						</template>
					</TableItem>
					<TableItem title="手工调整" :width="180">
						<template slot-scope="{data}">
							<div class="adjust-cell">
								<input
									v-model.trim="data.manualTargetCode"
									type="text"
									class="h-input adjust-input"
									:placeholder="data.targetCode || '编码'"
								>
								<i v-if="data.manualTargetCode" class="h-icon-close adjust-clear" @click="clearRowManualTarget(data)"></i>
							</div>
						</template>
					</TableItem>
				</Table>

				<div class="tip-line">预演在后端事务内执行并回滚，不会写入数据库。调整映射后请重新预演。</div>
			</div>

			<!-- 空状态 -->
			<div class="panel-card" v-if="!previewPlan && !previewLoading">
				<div class="empty-block">
					<i class="h-icon-upload empty-icon"></i>
					<div class="empty-title">上传科目余额表开始初始化</div>
					<div class="empty-desc">系统会自动识别余额表结构，将科目和期末余额导入为新账套的科目和期初余额</div>
				</div>
			</div>
		</div>
	</app-content>
</template>

<script>
import {mapState} from 'vuex';
import {MAX_SUBJECT_LEVEL} from '@/utils/subjectEncoding';

export default {
	name: "InitialImportCenter",
	data() {
		return {
			maxSubjectLevel: MAX_SUBJECT_LEVEL,
			balanceFile: null,
			balanceFileName: '',
			fileSizeText: '',
			isDragging: false,
			previewPlan: null,
			previewMappings: [],
			manualTargetMap: {},
			previewLoading: false,
			previewError: '',
			executeLoading: false,
			keyword: '',
			strategyFilter: 'all',
			onlyNeedConfirm: false,
			batchTargetCode: '',
			subjectLookupKeyword: '',
			subjectLookupOptions: [],
			subjectLookupLoading: false,
			strategyOptions: [
				{key: 'all', title: '全部策略'},
				{key: 'exact_code', title: '编码命中'},
				{key: 'exact_name', title: '名称命中'},
				{key: 'auto_create', title: '自动新增'},
				{key: 'manual_override', title: '手工指定'}
			]
		};
	},
	computed: {
		...mapState(['currentAccountSets']),
		step() {
			if (!this.balanceFile) return 1;
			if (!this.previewPlan) return 1;
			if (!this.previewPlan.trialBalanced) return 2;
			return 3;
		},
		canExecute() {
			return this.previewPlan && this.previewPlan.trialBalanced === true && this.balanceFile;
		},
		filteredMappings() {
			let list = this.previewMappings || [];
			let keyword = (this.keyword || '').trim();
			let filterKey = this.strategyFilter;
			let onlyNeedConfirm = this.onlyNeedConfirm;
			return list.filter(item => {
				if (onlyNeedConfirm && !item.needConfirm) {
					return false;
				}
				if (filterKey !== 'all' && item.strategy !== filterKey) {
					return false;
				}
				if (!keyword) {
					return true;
				}
				let text = [
					item.sourceCode,
					item.sourceName,
					item.targetCode,
					item.targetName,
					item.manualTargetCode
				].filter(Boolean).join('|');
				return text.indexOf(keyword) > -1;
			});
		},
		statusClass() {
			if (this.previewLoading) return 'status-loading';
			if (this.previewError) return 'status-error';
			if (!this.previewPlan) return 'status-wait';
			return this.previewPlan.trialBalanced ? 'status-ok' : 'status-warn';
		},
		statusIcon() {
			if (this.previewLoading) return 'h-icon-loading';
			if (this.previewError) return 'h-icon-close';
			if (!this.previewPlan) return 'h-icon-info';
			return this.previewPlan.trialBalanced ? 'h-icon-check' : 'h-icon-warn';
		},
		statusTitle() {
			if (this.previewLoading) return '正在预演分析...';
			if (this.previewError) return '预演失败';
			if (!this.previewPlan) return '等待上传文件并预演';
			return this.previewPlan.trialBalanced ? '预演通过，可执行导入' : '预演完成，试算不平衡';
		}
	},
	methods: {
		goBack() {
			this.$router.push('/initial');
		},
		triggerBalanceUpload() {
			this.$refs.balanceFile.click();
		},
		handleDrop(event) {
			this.isDragging = false;
			let files = event.dataTransfer.files;
			if (!files || !files.length) return;
			let file = files[0];
			let name = (file.name || '').toLowerCase();
			if (!name.endsWith('.xls') && !name.endsWith('.xlsx')) {
				this.$Message.warning('请上传 Excel 文件（.xls 或 .xlsx）');
				return;
			}
			this.setFile(file);
		},
		balanceFileChange() {
			if (!this.$refs.balanceFile.files.length) return;
			let file = this.$refs.balanceFile.files[0];
			this.$refs.balanceFile.value = "";
			this.setFile(file);
		},
		setFile(file) {
			this.balanceFile = file;
			this.balanceFileName = file.name;
			this.fileSizeText = this.formatFileSize(file.size);
			this.previewPlan = null;
			this.previewMappings = [];
			this.previewError = '';
			this.manualTargetMap = {};
			// 自动触发预演
			this.$nextTick(() => {
				this.runPreview(false);
			});
		},
		clearFile() {
			this.balanceFile = null;
			this.balanceFileName = '';
			this.fileSizeText = '';
			this.previewPlan = null;
			this.previewMappings = [];
			this.previewError = '';
			this.manualTargetMap = {};
		},
		formatFileSize(bytes) {
			if (!bytes) return '';
			if (bytes < 1024) return bytes + ' B';
			if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
			return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
		},
		formatMappingStrategy(strategy) {
			let dict = {
				exact_code: '编码命中',
				exact_name: '名称命中',
				auto_create: '自动新增',
				manual_override: '手工指定'
			};
			return dict[strategy] || strategy || '-';
		},
		getAjaxErrorMessage(error, fallback = '未知错误') {
			if (!error) return fallback;
			return error.msg || error.message || fallback;
		},
		ensureFileReady() {
			if (!this.balanceFile) {
				throw new Error("请先选择科目余额表文件");
			}
		},
		collectManualMappings() {
			let map = Object.assign({}, this.manualTargetMap);
			(this.previewMappings || []).forEach(item => {
				let sourceCode = item.sourceCode;
				if (!sourceCode) return;
				let manual = (item.manualTargetCode || '').trim();
				if (manual) {
					map[sourceCode] = manual;
				} else {
					delete map[sourceCode];
				}
			});
			this.manualTargetMap = map;
			return map;
		},
		buildFormData() {
			let formData = new FormData();
			formData.append('balanceFile', this.balanceFile);
			let mappingAdjustments = this.collectManualMappings();
			if (Object.keys(mappingAdjustments).length) {
				formData.append('mappingAdjustments', JSON.stringify(mappingAdjustments));
			}
			return formData;
		},
		applyPreviewResult(data) {
			this.previewPlan = data || null;
			let rows = (data && data.mappingRows) ? data.mappingRows : [];
			this.previewMappings = rows.map(row => {
				let sourceCode = row.sourceCode;
				let manualTargetCode = sourceCode && this.manualTargetMap[sourceCode] ? this.manualTargetMap[sourceCode] : '';
				return Object.assign({}, row, {manualTargetCode});
			});
		},
		async runPreview(silent) {
			try {
				this.previewLoading = true;
				this.previewError = '';
				this.ensureFileReady();
				let formData = this.buildFormData();
				let {data} = await this.$api.setting.initialBalance.previewBalanceSheetInit(formData);
				this.applyPreviewResult(data);
				if (!silent) {
					if (data && data.trialBalanced === false) {
						this.$Message.warning("预演完成：试算不平衡，请检查映射明细");
					} else {
						this.$Message.success("预演通过，可执行导入");
					}
				}
				return data;
			} catch (error) {
				this.previewError = this.getAjaxErrorMessage(error);
				if (!silent) {
					this.$Message.error("导入预演失败: " + this.previewError);
				}
				throw error;
			} finally {
				this.previewLoading = false;
			}
		},
		confirmAndExecute() {
			if (!this.canExecute) return;
			let plan = this.previewPlan;
			let msg = `确认执行导入？\n\n将导入 ${plan.importRowTotal || 0} 个末级科目，${plan.initialBalanceTotal || 0} 条期初余额。`;
			if (plan.encodingUpgraded) {
				msg += `\n编码规则将升级为 ${plan.encodingAfter}。`;
			}
			if (plan.mappingSummary && plan.mappingSummary.autoCreate > 0) {
				msg += `\n将自动新增 ${plan.mappingSummary.autoCreate} 个科目。`;
			}
			msg += '\n\n此操作不可撤销，请确认。';
			this.$Confirm(msg).then(() => {
				this.startExecute();
			});
		},
		async startExecute() {
			try {
				this.executeLoading = true;
				this.ensureFileReady();

				let previewData = await this.runPreview(true);
				if (!previewData || previewData.trialBalanced === false) {
					this.$Message.warning("试算不平衡，已阻止执行。请先调整映射。");
					return;
				}

				let formData = this.buildFormData();
				await this.$api.setting.initialBalance.executeBalanceSheetInit(formData);
				await this.$store.dispatch('init');
				this.$Message.success("初始化导入成功！科目和期初余额已就绪，可以开始记账。");
				setTimeout(() => {
					this.$router.push('/initial');
				}, 1500);
			} catch (error) {
				this.$Message.error("执行失败: " + this.getAjaxErrorMessage(error));
			} finally {
				this.executeLoading = false;
			}
		},
		applyBatchTarget() {
			let code = (this.batchTargetCode || '').trim();
			if (!code) {
				this.$Message.warning("请先输入批量目标科目编码");
				return;
			}
			let list = this.filteredMappings || [];
			if (!list.length) {
				this.$Message.warning("当前筛选结果为空");
				return;
			}
			list.forEach(item => {
				item.manualTargetCode = code;
			});
			this.collectManualMappings();
			this.$Message.success(`已批量填充 ${list.length} 条记录`);
		},
		clearAllManualTarget() {
			(this.previewMappings || []).forEach(item => {
				item.manualTargetCode = '';
			});
			this.manualTargetMap = {};
			this.$Message.success("已清空所有手工调整");
		},
		clearRowManualTarget(row) {
			row.manualTargetCode = '';
			this.collectManualMappings();
		},
		async searchSubjects() {
			let keyword = (this.subjectLookupKeyword || '').trim();
			if (!keyword || keyword.length < 2) {
				this.$Message.warning("请输入至少2位编码或名称");
				return;
			}
			try {
				this.subjectLookupLoading = true;
				let {data} = await this.$api.setting.subject.voucherSelect({keyword: keyword});
				this.subjectLookupOptions = (data || []).map(item => ({
					code: item.code || item.subjectCode,
					name: item.name || item.subjectName || '',
					balanceDirection: item.balanceDirection || ''
				}));
			} catch (error) {
				this.subjectLookupOptions = [];
				this.$Message.error("科目检索失败: " + this.getAjaxErrorMessage(error));
			} finally {
				this.subjectLookupLoading = false;
			}
		},
		useLookupSubject(item) {
			if (!item || !item.code) return;
			this.batchTargetCode = item.code;
			this.$Message.success(`已带入：${item.code} ${item.name}`);
		}
	}
};
</script>

<style lang="less" scoped>
.initial-import-center {
	.import-main {
		padding-top: 8px;
		max-width: 1200px;
	}

	/* 步骤条 */
	.steps-bar {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 16px 0 20px;
		gap: 0;
	}

	.step {
		display: flex;
		align-items: center;
		gap: 8px;
		opacity: 0.4;
		transition: opacity 0.3s;

		&.active {
			opacity: 1;
		}

		&.done .step-num {
			background: #52c41a;
			border-color: #52c41a;
		}
	}

	.step-num {
		width: 28px;
		height: 28px;
		border-radius: 50%;
		border: 2px solid #1890ff;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 13px;
		font-weight: 600;
		color: #1890ff;
		background: #fff;

		.active & {
			background: #1890ff;
			color: #fff;
		}
	}

	.step-text {
		font-size: 14px;
		color: #333;
		font-weight: 500;
	}

	.step-line {
		width: 80px;
		height: 2px;
		background: #e5e7eb;
		margin: 0 12px;
		transition: background 0.3s;

		&.active {
			background: #1890ff;
		}
	}

	/* 卡片 */
	.panel-card {
		border: 1px solid #e7e7e7;
		border-radius: 8px;
		background: #fff;
		padding: 20px;
		margin-bottom: 16px;
	}

	.card-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 16px;
	}

	.card-title {
		font-size: 16px;
		font-weight: 600;
		color: #1a1a1a;
	}

	.encoding-tag {
		font-size: 12px;
		color: #666;
		background: #f5f5f5;
		padding: 2px 10px;
		border-radius: 12px;
	}

	/* 上传区域 */
	.upload-zone {
		border: 2px dashed #d9d9d9;
		border-radius: 8px;
		padding: 32px;
		text-align: center;
		cursor: pointer;
		transition: all 0.2s;
		margin-bottom: 16px;
		background: #fafafa;

		&:hover, &.dragging {
			border-color: #1890ff;
			background: #f0f7ff;
		}

		&.has-file {
			border-style: solid;
			border-color: #d9d9d9;
			padding: 16px 20px;
			cursor: default;
			background: #fff;
		}
	}

	.upload-placeholder {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 8px;
	}

	.upload-icon {
		font-size: 36px;
		color: #1890ff;
	}

	.upload-text {
		font-size: 15px;
		color: #333;
	}

	.upload-hint {
		font-size: 12px;
		color: #999;
	}

	.upload-file-info {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.upload-file-icon {
		font-size: 28px;
		color: #52c41a;
	}

	.upload-file-detail {
		flex: 1;
		text-align: left;
	}

	.upload-file-name {
		font-size: 14px;
		color: #333;
		font-weight: 500;
		word-break: break-all;
	}

	.upload-file-size {
		font-size: 12px;
		color: #999;
		margin-top: 2px;
	}

	/* 状态框 */
	.status-box {
		padding: 12px 16px;
		border-radius: 6px;
		margin-bottom: 16px;
	}

	.status-title {
		font-size: 14px;
		font-weight: 600;
		display: flex;
		align-items: center;
		gap: 6px;
	}

	.status-reason {
		font-size: 12px;
		line-height: 20px;
		margin-top: 6px;
		color: #8c4700;
	}

	.status-meta {
		font-size: 12px;
		color: #666;
		margin-top: 4px;
	}

	.upgrade-notice {
		color: #1890ff;
		font-weight: 500;
	}

	.status-wait {
		background: #f6f7f8;
		border: 1px solid #e5e7eb;
		.status-title { color: #8c8c8c; }
	}

	.status-loading {
		background: #e6f7ff;
		border: 1px solid #91d5ff;
		.status-title { color: #1890ff; }
	}

	.status-ok {
		background: #f0fff4;
		border: 1px solid #b7eb8f;
		.status-title { color: #389e0d; }
	}

	.status-warn {
		background: #fffbe6;
		border: 1px solid #ffe58f;
		.status-title { color: #d48806; }
	}

	.status-error {
		background: #fff2f0;
		border: 1px solid #ffccc7;
		.status-title { color: #cf1322; }
	}

	/* 汇总网格 */
	.summary-grid {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		grid-gap: 10px;
		margin-bottom: 16px;
	}

	.summary-item {
		padding: 10px 12px;
		border: 1px solid #f0f0f0;
		border-radius: 6px;
		background: #fafafa;

		&.has-warning {
			border-color: #ffd8bf;
			background: #fff7f0;
		}

		&.span-full {
			grid-column: 1 / -1;
			text-align: center;
		}

		&.balance-ok {
			background: #f0fff4;
			border-color: #b7eb8f;
		}

		&.balance-warn {
			background: #fffbe6;
			border-color: #ffe58f;
		}
	}

	.summary-label {
		font-size: 12px;
		color: #8c8c8c;
	}

	.summary-value {
		font-size: 15px;
		font-weight: 600;
		margin-top: 4px;
	}

	/* 操作按钮 */
	.card-actions {
		display: flex;
		gap: 10px;
		margin-top: 4px;
	}

	/* 工具栏 */
	.toolbar-row {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 10px;
	}

	.toolbar-input {
		width: 300px;
	}

	.toolbar-select {
		width: 160px;
	}

	/* 查找列表 */
	.lookup-list {
		margin: 0 0 12px;
		max-height: 200px;
		overflow-y: auto;
		border: 1px solid #f0f0f0;
		border-radius: 6px;
	}

	.lookup-item {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 8px 12px;
		cursor: pointer;
		border-bottom: 1px solid #f5f5f5;
		transition: background 0.15s;

		&:hover {
			background: #f0f7ff;
		}

		&:last-child {
			border-bottom: none;
		}
	}

	.lookup-code {
		font-family: Menlo, Monaco, Consolas, "Liberation Mono", monospace;
		font-size: 12px;
		font-weight: 600;
		color: #1890ff;
	}

	.lookup-name {
		flex: 1;
		font-size: 12px;
		color: #444;
	}

	.lookup-dir {
		font-size: 12px;
		color: #999;
	}

	/* 映射表格 */
	.mapping-table {
		margin-top: 6px;
	}

	.subject-line {
		display: flex;
		align-items: center;
		gap: 4px;
		overflow: hidden;
	}

	.subject-code {
		font-family: Menlo, Monaco, Consolas, "Liberation Mono", monospace;
		font-size: 12px;
		font-weight: 600;
		white-space: nowrap;
		color: #333;
	}

	.auto-create-code {
		color: #1890ff;
	}

	.subject-name {
		flex: 1;
		font-size: 12px;
		color: #666;
		white-space: nowrap;
		text-overflow: ellipsis;
		overflow: hidden;
	}

	.strategy-tag {
		font-size: 11px;
		padding: 1px 6px;
		border-radius: 3px;
		white-space: nowrap;
	}

	.strategy-exact_code {
		background: #e6f7ff;
		color: #1890ff;
	}

	.strategy-exact_name {
		background: #f0fff4;
		color: #52c41a;
	}

	.strategy-auto_create {
		background: #fff7e6;
		color: #fa8c16;
	}

	.strategy-manual_override {
		background: #f9f0ff;
		color: #722ed1;
	}

	.dir-debit {
		color: #1890ff;
		font-weight: 500;
	}

	.dir-credit {
		color: #f5222d;
		font-weight: 500;
	}

	.zero-amount {
		color: #d9d9d9;
	}

	.adjust-cell {
		display: flex;
		align-items: center;
		position: relative;
	}

	.adjust-input {
		padding-right: 24px;
	}

	.adjust-clear {
		position: absolute;
		right: 6px;
		cursor: pointer;
		color: #999;
		font-size: 12px;

		&:hover {
			color: #f5222d;
		}
	}

	/* 空状态 */
	.empty-block {
		padding: 48px 20px;
		text-align: center;
	}

	.empty-icon {
		font-size: 48px;
		color: #d9d9d9;
		margin-bottom: 12px;
	}

	.empty-title {
		font-size: 16px;
		color: #333;
		font-weight: 500;
		margin-bottom: 8px;
	}

	.empty-desc {
		font-size: 13px;
		color: #999;
		max-width: 400px;
		margin: 0 auto;
		line-height: 1.6;
	}

	/* 公用文本样式 */
	.tip-line {
		margin-top: 12px;
		font-size: 12px;
		color: #8c8c8c;
	}

	.muted-text {
		font-size: 12px;
		color: #8c8c8c;
	}

	.warning-text {
		color: #d4380d;
	}

	.ok-text {
		color: #389e0d;
	}

	.text-link {
		color: #1890ff;
		cursor: pointer;
		font-size: 13px;

		&:hover {
			text-decoration: underline;
		}
	}
}
</style>
